/**
 * Operator Verb Dictionary
 *
 * Phase 8 Prompt 3 — per audit deliverable 04D Section C.
 *
 * Single source of truth for every operator-facing button label across the
 * platform. Anti-pattern labels (Submit, Execute, Initiate, Suspend, etc.)
 * are explicitly listed and FORBIDDEN — surface designs that need an action
 * must pick a verb from this dictionary or extend it via PR + Grant review.
 *
 * Voice (from 04B Section K): plain English, sixth-grade reading level,
 * never patronizing.
 *
 * Usage:
 *   import { OPERATOR_VERBS, OperatorVerb } from "../constants/operatorVerbs";
 *   const verb = OPERATOR_VERBS.send_now;  // typed: { label, icon, ... }
 *   <ActionButton verb="send_now" onClick={fn} />
 */

export type OperatorVerb =
  | "send_now"
  | "schedule"
  | "pause"
  | "resume"
  | "activate"
  | "archive"
  | "send_test_to_me"
  | "duplicate"
  | "delete"
  | "preview"
  | "see_recipients"
  | "save_draft"
  | "publish"
  | "approve"
  | "reject"
  | "edit"
  | "cancel"
  | "undo"
  | "export_csv"
  | "import"
  | "sync_to_brevo"
  | "refresh"
  | "search"
  | "reclassify_kind"
  | "mark_as_winner"
  | "view_queue_position"
  | "change_test_recipient"
  | "force_exit_enrollment"
  | "view_analytics"
  | "view_members";

export interface OperatorVerbConfig {
  /** Verb id used in code. */
  id: OperatorVerb;
  /** UI label — operator sees this on the button. */
  label: string;
  /** Operator intent ("what they'd say out loud"). For dev / audit reference. */
  intent: string;
  /** Material Icons icon name. */
  icon: string;
  /** Anti-pattern labels — never use these. */
  antiPatterns: string[];
  /** Visual variant. */
  variant: "primary" | "secondary" | "destructive" | "ghost";
  /** Whether this action requires a confirmation dialog. */
  requiresConfirm?: boolean;
  /** Optional keyboard shortcut (display only; actual binding is per-surface). */
  shortcut?: string;
}

/**
 * Master operator verb table. Every Phase 5/6 surface picks from these.
 */
export const OPERATOR_VERBS: Record<OperatorVerb, OperatorVerbConfig> = {
  send_now: {
    id: "send_now",
    label: "Send Now",
    intent: "Send this now",
    icon: "send",
    antiPatterns: ["Submit", "Execute", "Initiate Send", "Fire"],
    variant: "primary",
    requiresConfirm: true,
    shortcut: "Cmd+Enter",
  },
  schedule: {
    id: "schedule",
    label: "Schedule",
    intent: "Schedule it for later",
    icon: "schedule",
    antiPatterns: ["Defer", "Queue", "Set Send Time"],
    variant: "primary",
  },
  pause: {
    id: "pause",
    label: "Pause",
    intent: "Pause this funnel/campaign",
    icon: "pause",
    antiPatterns: ["Suspend", "Deactivate", "Disable Enrollment", "Stop"],
    variant: "secondary",
  },
  resume: {
    id: "resume",
    label: "Resume",
    intent: "Resume this funnel/campaign",
    icon: "play_arrow",
    antiPatterns: ["Activate", "Restart", "Re-enable"],
    variant: "secondary",
  },
  activate: {
    id: "activate",
    label: "Activate",
    intent: "Start the funnel",
    icon: "play_arrow",
    antiPatterns: ["Enable", "Set Status to Active", "Turn On"],
    variant: "primary",
    requiresConfirm: true,
  },
  archive: {
    id: "archive",
    label: "Archive",
    intent: "Stop the funnel for good",
    icon: "archive",
    antiPatterns: ["Soft-delete", "Disable", "Set Archived"],
    variant: "secondary",
    requiresConfirm: true,
  },
  send_test_to_me: {
    id: "send_test_to_me",
    label: "Send Test to Me",
    intent: "Try it on me first",
    icon: "mark_email_read",
    antiPatterns: ["Initiate Test Send", "QA Send", "Send Sample"],
    variant: "secondary",
    shortcut: "Cmd+T",
  },
  duplicate: {
    id: "duplicate",
    label: "Duplicate",
    intent: "Clone this and edit",
    icon: "content_copy",
    antiPatterns: ["Clone", "Instantiate Copy", "Copy"],
    variant: "secondary",
    shortcut: "Cmd+D",
  },
  delete: {
    id: "delete",
    label: "Delete",
    intent: "Throw it out",
    icon: "delete",
    antiPatterns: ["Hard-delete", "Remove Record", "Trash"],
    variant: "destructive",
    requiresConfirm: true,
  },
  preview: {
    id: "preview",
    label: "Preview",
    intent: "Show me what it'll look like",
    icon: "visibility",
    antiPatterns: ["Render Sample", "View Output"],
    variant: "ghost",
    shortcut: "Cmd+P",
  },
  see_recipients: {
    id: "see_recipients",
    label: "See Recipients",
    intent: "Who will get this?",
    icon: "people",
    antiPatterns: ["List Resolved Members", "Show Audience"],
    variant: "ghost",
  },
  save_draft: {
    id: "save_draft",
    label: "Save Draft",
    intent: "Save my work as a draft",
    icon: "save",
    antiPatterns: ["Save"],
    variant: "secondary",
    shortcut: "Cmd+S",
  },
  publish: {
    id: "publish",
    label: "Publish",
    intent: "Publish this campaign",
    icon: "publish",
    antiPatterns: ["Make Live", "Set to Live"],
    variant: "primary",
    requiresConfirm: true,
  },
  approve: {
    id: "approve",
    label: "Approve",
    intent: "Approve this for sending",
    icon: "check_circle",
    antiPatterns: ["Sign-off", "Authorize"],
    variant: "primary",
  },
  reject: {
    id: "reject",
    label: "Reject",
    intent: "Reject this draft",
    icon: "block",
    antiPatterns: ["Block", "Veto"],
    variant: "destructive",
    requiresConfirm: true,
  },
  edit: {
    id: "edit",
    label: "Edit",
    intent: "Edit this entity",
    icon: "edit",
    antiPatterns: ["Modify"],
    variant: "ghost",
    shortcut: "Cmd+E",
  },
  cancel: {
    id: "cancel",
    label: "Cancel",
    intent: "Cancel my work",
    icon: "close",
    antiPatterns: ["Abort", "Discard"],
    variant: "ghost",
    shortcut: "Esc",
  },
  undo: {
    id: "undo",
    label: "Undo",
    intent: "Undo last change",
    icon: "undo",
    antiPatterns: ["Rollback"],
    variant: "ghost",
    shortcut: "Cmd+Z",
  },
  export_csv: {
    id: "export_csv",
    label: "Export CSV",
    intent: "Export data",
    icon: "download",
    antiPatterns: ["Download", "Extract"],
    variant: "secondary",
  },
  import: {
    id: "import",
    label: "Import",
    intent: "Import data",
    icon: "upload",
    antiPatterns: ["Upload", "Load"],
    variant: "secondary",
  },
  sync_to_brevo: {
    id: "sync_to_brevo",
    label: "Sync to Brevo",
    intent: "Sync with provider",
    icon: "sync",
    antiPatterns: ["Push", "Upload", "Reconcile"],
    variant: "secondary",
  },
  refresh: {
    id: "refresh",
    label: "Refresh",
    intent: "Reload data",
    icon: "refresh",
    antiPatterns: ["Reload", "Re-fetch"],
    variant: "ghost",
    shortcut: "Cmd+R",
  },
  search: {
    id: "search",
    label: "Search",
    intent: "Find an entity",
    icon: "search",
    antiPatterns: ["Filter", "Query"],
    variant: "ghost",
    shortcut: "Cmd+K",
  },
  reclassify_kind: {
    id: "reclassify_kind",
    label: "Reclassify Kind…",
    intent: "Reclassify kind (HC-3 gated)",
    icon: "swap_horiz",
    antiPatterns: ["Change Kind", "Move to Marketing"],
    variant: "destructive",
    requiresConfirm: true,
  },
  mark_as_winner: {
    id: "mark_as_winner",
    label: "Mark as Winner",
    intent: "Mark as winner (A/B)",
    icon: "emoji_events",
    antiPatterns: ["Select Variant", "Pick A"],
    variant: "primary",
  },
  view_queue_position: {
    id: "view_queue_position",
    label: "View Queue Position",
    intent: "Show in queue",
    icon: "queue",
    antiPatterns: ["Show in Schedule", "See Pending"],
    variant: "ghost",
  },
  change_test_recipient: {
    id: "change_test_recipient",
    label: "Change Test Recipient",
    intent: "Send to a different test",
    icon: "mail",
    antiPatterns: ["Modify Test Email"],
    variant: "ghost",
  },
  force_exit_enrollment: {
    id: "force_exit_enrollment",
    label: "Force Exit",
    intent: "Manually exit a stuck enrollment",
    icon: "logout",
    antiPatterns: ["Kick User Out", "Remove from Flow"],
    variant: "destructive",
    requiresConfirm: true,
  },
  view_analytics: {
    id: "view_analytics",
    label: "View Analytics →",
    intent: "Drill into analytics",
    icon: "bar_chart",
    antiPatterns: ["See Stats", "Show Metrics"],
    variant: "ghost",
  },
  view_members: {
    id: "view_members",
    label: "View Members",
    intent: "Browse members in this audience",
    icon: "people",
    antiPatterns: ["List Users", "Show Member List"],
    variant: "ghost",
  },
};

/**
 * Lookup helper that throws in dev if the verb is unknown — catches typos
 * at component-render time rather than silently rendering the wrong button.
 */
export function getVerb(id: OperatorVerb): OperatorVerbConfig {
  const cfg = OPERATOR_VERBS[id];
  if (!cfg) {
    throw new Error(
      `[operatorVerbs] Unknown verb id "${id}". ` +
        `Pick from: ${Object.keys(OPERATOR_VERBS).join(", ")}`,
    );
  }
  return cfg;
}
